import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, Theme } from '@mui/material/styles';

// Extend the Material-UI theme to include custom properties
declare module '@mui/material/styles' {
    interface Palette {
        custom?: {
            navbarBackground: string;
            navbarBorder: string;
            bodyBackground: string;
        };
    }
    interface PaletteOptions {
        custom?: {
            navbarBackground: string;
            navbarBorder: string;
            bodyBackground: string;
        };
    }
}

interface ThemeContextType {
    mode: 'light' | 'dark';
    toggleTheme: () => void;
    theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        // Check localStorage for saved preference, default to dark
        const savedMode = localStorage.getItem('themeMode');
        return (savedMode as 'light' | 'dark') || 'dark';
    });

    const createAppTheme = (mode: 'light' | 'dark'): Theme => {
        return createTheme({
            palette: {
                mode,
                background: {
                    default: mode === 'dark' ? '#111112' : '#f8f9fa',
                    paper: mode === 'dark' ? '#18191b' : '#ffffff',
                },
                primary: {
                    main: '#bc2222',
                    dark: mode === 'dark' ? '#8b1a1a' : '#8b1a1a',
                },
                secondary: {
                    main: '#bc2222',
                    dark: '#8b1a1a',
                },
                text: {
                    primary: mode === 'dark' ? '#ffffff' : '#1a1a1a',
                    secondary: mode === 'dark' ? '#b0b0b0' : '#666666',
                },
                divider: mode === 'dark' ? '#333333' : '#e0e0e0',
                // Add custom colors for better navbar separation
                custom: {
                    navbarBackground: mode === 'dark' ? 'rgba(20,20,20,0.95)' : '#fafbfc',
                    navbarBorder: mode === 'dark' ? '#333333' : '#e1e5e9',
                    bodyBackground: mode === 'dark' ? '#111112' : '#f5f5f5',
                },
            },
            typography: {
                fontFamily: 'Inter, Arial, sans-serif',
            },
            components: {
                MuiCard: {
                    styleOverrides: {
                        root: {
                            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
                            border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
                            border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                        },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: mode === 'dark' ? '#18191b' : '#fafbfc',
                            borderBottom: `1px solid ${mode === 'dark' ? '#333333' : '#e1e5e9'}`,
                            color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
                            boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)',
                        },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
                            borderRight: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: mode === 'dark' ? '#333333' : '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#bc2222',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#bc2222',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: mode === 'dark' ? '#b0b0b0' : '#666666',
                                '&.Mui-focused': {
                                    color: '#bc2222',
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
                            },
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        root: {
                            color: mode === 'dark' ? '#ffffff' : '#1a1a1a',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: mode === 'dark' ? '#333333' : '#e0e0e0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#bc2222',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#bc2222',
                            },
                        },
                    },
                },
            },
        });
    };

    const theme = createAppTheme(mode);

    const toggleTheme = () => {
        const newMode = mode === 'dark' ? 'light' : 'dark';
        setMode(newMode);
        localStorage.setItem('themeMode', newMode);
    };

    useEffect(() => {
        // Update body background color when theme changes
        document.body.style.backgroundColor = mode === 'dark' ? '#111112' : '#f8f9fa';
    }, [mode]);

    const value = {
        mode,
        toggleTheme,
        theme,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}; 